import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

// ── Types matching ACTUAL API responses ──────────────────────

// From GET /v1/campaigns/:id/slots (slot-analytics.ts)
export interface SlotMetrics {
  slot: string
  type: 'article' | 'ad'
  clicks: number
  unique_clicks: number
  ctr: number
}

export interface TopLink {
  url: string
  slot: string
  clicks: number
  unique_clicks: number
}

export interface CampaignSlotsResponse {
  campaign_id: string
  total_sent: number
  slots: SlotMetrics[]
  top_links: TopLink[]
}

// From GET /v1/analytics/advertisers (analytics-advertisers.ts)
export interface AdvertiserMetrics {
  domain: string
  clicks: number
  unique_clicks: number
  campaigns: number
  ctr: number
  first_seen: string
}

export interface AdvertiserCampaignBreakdown {
  campaign_id: string
  campaign_name: string
  sent_at: string
  sent: number
  clicks: number
  ctr: number
}

export interface AdvertiserAnalyticsResponse {
  advertisers: AdvertiserMetrics[]
  by_campaign: AdvertiserCampaignBreakdown[]
}

export interface AdvertiserAnalyticsParams {
  from: string
  to: string
  advertiser?: string
}

// From POST /v1/analytics/export (analytics-export.ts)
export interface ExportRequest {
  type: 'campaign' | 'advertiser'
  campaign_id?: string
  advertiser_domain?: string
  from: string
  to: string
  format: 'csv' | 'pdf'
  include_bots?: boolean
}

// ── Hooks ──────────────────────────────────────────────────────

const SLOTS_KEY = ['campaign-slots'] as const
const ADVERTISERS_KEY = ['advertiser-analytics'] as const

export function useCampaignSlots(
  campaignId: string,
): ReturnType<typeof useQuery<CampaignSlotsResponse>> {
  return useQuery({
    queryKey: [...SLOTS_KEY, campaignId],
    queryFn: () => apiClient.get<CampaignSlotsResponse>(endpoints.campaigns.slots(campaignId)),
    enabled: !!campaignId,
  })
}

export function useAdvertiserAnalytics(
  params: AdvertiserAnalyticsParams,
): ReturnType<typeof useQuery<AdvertiserAnalyticsResponse>> {
  return useQuery({
    queryKey: [...ADVERTISERS_KEY, params],
    queryFn: () => apiClient.get<AdvertiserAnalyticsResponse>(endpoints.advertisers(params)),
    enabled: !!params.from && !!params.to,
  })
}

export function useAnalyticsExport(): ReturnType<typeof useMutation<void, unknown, ExportRequest>> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''

  return useMutation({
    mutationFn: async (data: ExportRequest) => {
      const response = await fetch(`${baseUrl}${endpoints.analyticsExport}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`Export failed (${response.status}): ${text}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      const isPdf = data.format === 'pdf'
      const ext = isPdf ? 'pdf' : 'csv'
      const timestamp = new Date().toISOString().slice(0, 10)
      anchor.download = `analytics-export-${timestamp}.${ext}`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(url)
    },
    onError: (error: unknown) => {
      const msg = error instanceof Error ? error.message : 'Export failed'
      toast.error(msg)
    },
  })
}
